import { Box, MenuItem, Typography } from "@mui/material";
import { Form, Formik, useFormikContext } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { ReactElement, useMemo } from "react";
import * as Yup from "yup";
import { z } from "zod";

import {
  FeatureFlagDefinition,
  FeatureFlagGroup,
  FeatureFlagKey,
  FeatureFlags,
  PartialFeatureFlags,
  featureFlagDefinitions,
  featureFlagGroups,
  parseFeatureFlags,
  parsePartialFeatureFlags,
} from "@stw/feature-flags";

import {
  EditTenantFeatureFlagsMutation,
  EditTenantFeatureFlagsMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import FormikSwitchField from "~frontend/components/forms/FormikSwitchField";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { EDIT_TENANT_FEATURE_FLAGS } from "~frontend/graphql/tenant";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import {
  Tenant,
  useDefaultFeatureFlags,
  useTenant,
} from "~frontend/providers/LoggedInUserProvider/context";
import { useNonCacheClearingApolloClient } from "~frontend/providers/NonCacheClearingApolloClientProvider";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

type FlagType<T extends FeatureFlagDefinition["type"]> = {
  zodType: T;
  yupSchema: Yup.AnySchema;
  valueToFormValue: (value: z.infer<T>) => string;
  formValueToValue: (value: string) => z.infer<T>;
  component: React.FC<{
    flag: FeatureFlagDefinition & { key: string };
    override: boolean;
  }>;
};

const FeatureFlagComponents: {
  [K in FeatureFlagDefinition["type"]["_def"]["typeName"]]: FlagType<
    Extract<FeatureFlagDefinition["type"], { _def: { typeName: K } }>
  >;
} = {
  ZodString: {
    zodType: z.string(),
    yupSchema: Yup.string(),
    formValueToValue(value) {
      return value;
    },
    valueToFormValue(value) {
      return value;
    },
    component: ({ flag, override }) => {
      return (
        <FormikTextField
          sx={{ pt: 0 }}
          name={`flagValues.${flag.key}`}
          disabled={!override}
        />
      );
    },
  },
  ZodNumber: {
    zodType: z.number(),
    yupSchema: Yup.number().typeError("Must be a number"),
    formValueToValue(value) {
      return Number(value);
    },
    valueToFormValue(value) {
      return String(value);
    },
    component: ({ flag, override }) => {
      return (
        <FormikTextField
          sx={{ pt: 0 }}
          name={`flagValues.${flag.key}`}
          disabled={!override}
        />
      );
    },
  },
  ZodEnum: {
    zodType: z.enum([""]),
    yupSchema: Yup.string(),
    formValueToValue(value) {
      return value;
    },
    valueToFormValue(value) {
      return value;
    },
    component: ({ flag, override }) => {
      return (
        <FormikSelect
          sx={{ pt: 0, minWidth: "400px" }}
          name={`flagValues.${flag.key}`}
          disabled={!override}
        >
          {(flag.type as z.ZodEnum<any>)._def.values.map((value: string) => (
            <MenuItem value={value} key={value}>
              {value}
            </MenuItem>
          ))}
        </FormikSelect>
      );
    },
  },
  ZodBoolean: {
    zodType: z.boolean(),
    yupSchema: Yup.string().oneOf(["true", "false"]).required(),
    formValueToValue(value) {
      return value === "true";
    },
    valueToFormValue(value) {
      return value ? "true" : "false";
    },
    component: ({ flag, override }) => {
      return (
        <FormikSelect
          sx={{ pt: 0, minWidth: "400px" }}
          name={`flagValues.${flag.key}`}
          disabled={!override}
        >
          <MenuItem value="true">true</MenuItem>
          <MenuItem value="false">false</MenuItem>
        </FormikSelect>
      );
    },
  },
};

function FeatureFlagGroupSection({
  group,
  defaults,
}: {
  group: FeatureFlagGroup;
  defaults: FeatureFlags;
}) {
  const formik = useFormikContext<FormValues>();

  const flags = Object.entries(featureFlagDefinitions)
    .map(([key, flag]) => ({ key, ...flag }))
    .filter(flag => flag.group === group.key);

  if (flags.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h1Bold">{group.title}</Typography>
      <Typography sx={{ mb: 2 }}>{group.description}</Typography>
      {flags.map(flag => {
        const Component =
          FeatureFlagComponents[flag.type._def.typeName].component;

        const defaultValue = defaults[flag.key as FeatureFlagKey];

        return (
          <Box sx={{ mb: 2 }} key={flag.key}>
            <FlexBox flexDirection="row" alignItems="center">
              <Typography variant="h2" sx={{ mr: 1 }}>
                {flag.name} (
                <Typography display="inline" sx={{ fontFamily: "monospace" }}>
                  {flag.key}
                </Typography>
                )
              </Typography>
              <FormikSwitchField
                name={`overrides.${flag.key}`}
                label={`Override (default: ${defaultValue})`}
                switchProps={{
                  onChange: () => {
                    formik
                      .setFieldValue(`flagValues.${flag.key}`, defaultValue)
                      .then(
                        () =>
                          formik.validateField(`flagValues.${flag.key}`) as any
                      )
                      .catch(console.error);
                  },
                }}
              />
            </FlexBox>
            <Typography>{flag.description}</Typography>
            <Component
              flag={flag}
              key={flag.key}
              override={formik.values.overrides[flag.key as FeatureFlagKey]}
            />
          </Box>
        );
      })}
    </Box>
  );
}

type FormValues = {
  flagValues: FeatureFlags;
  overrides: { [K in FeatureFlagKey]: boolean };
  [submitErrorField]: string | null;
};

const yupSchema = Yup.object().shape({
  flagValues: Yup.object().shape(
    Object.fromEntries(
      Object.entries(featureFlagDefinitions).map(([key, flag]) => [
        key,
        FeatureFlagComponents[flag.type._def.typeName].yupSchema,
      ])
    )
  ),
  overrides: Yup.object().shape(
    Object.fromEntries(
      Object.keys(featureFlagDefinitions).map(key => [key, Yup.boolean()])
    )
  ),
});

function buildFormValues(tenant: Tenant, defaults: FeatureFlags): FormValues {
  const flagValues = parseFeatureFlags(tenant.featureFlags, defaults);
  const flagOverrides = parsePartialFeatureFlags(tenant.featureFlags);

  return {
    [submitErrorField]: null,
    flagValues: _.mapValues(flagValues, (value, key) => {
      const flag = featureFlagDefinitions[key as FeatureFlagKey];
      return (
        FeatureFlagComponents[flag.type._def.typeName] as any
      ).valueToFormValue(value);
    }),
    overrides: Object.fromEntries(
      Object.keys(flagValues).map(key => [
        key,
        flagOverrides[key as FeatureFlagKey] !== undefined,
      ])
    ) as { [K in FeatureFlagKey]: boolean },
  };
}

export default function OrgExports(): ReactElement {
  const tenant = useTenant()!;
  const getErrorMessage = useErrorMessageFormatter();
  const snackbar = useSnackbar();
  const client = useNonCacheClearingApolloClient();
  const defaultFeatureFlags = useDefaultFeatureFlags();

  useBreadcrumbs([
    {
      label: "Feature Flags",
      path: "/organization/flags",
    },
  ]);

  const [editTenantFeatureFlags] = useMutation<
    EditTenantFeatureFlagsMutation,
    EditTenantFeatureFlagsMutationVariables
  >(EDIT_TENANT_FEATURE_FLAGS, {
    client,
  });

  const initialValues = useMemo(
    () => buildFormValues(tenant, defaultFeatureFlags),
    [tenant, defaultFeatureFlags]
  );

  return (
    <PageContainer>
      <PageHeader>Feature Flags (Staff Only)</PageHeader>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values, formik) => {
          const newOverrides: PartialFeatureFlags = {};

          Object.keys(values.overrides).forEach(key => {
            if (values.overrides[key as FeatureFlagKey]) {
              newOverrides[key as FeatureFlagKey] = (
                FeatureFlagComponents[
                  featureFlagDefinitions[key as FeatureFlagKey].type._def
                    .typeName
                ] as any
              ).formValueToValue(values.flagValues[key as FeatureFlagKey]);
            }
          });

          const { errors, data } = await editTenantFeatureFlags({
            variables: {
              input: {
                featureFlags: JSON.stringify(newOverrides),
              },
            },
          });

          if (errors) {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(errors),
            });
          } else if (data) {
            formik.resetForm({
              values: buildFormValues(
                data.editTenantFeatureFlags,
                defaultFeatureFlags
              ),
            });
            snackbar.enqueueSnackbar("Organization feature flags updated", {
              variant: "success",
            });
          }
        }}
        validationSchema={yupSchema}
      >
        <Form>
          <FormikErrorBanner />
          {featureFlagGroups
            .filter(group => group.key !== "hidden")
            .map(group => (
              <FeatureFlagGroupSection
                group={group}
                key={group.key}
                defaults={defaultFeatureFlags}
              />
            ))}
          <FormikSubmitButton submitText="Save" />
        </Form>
      </Formik>
    </PageContainer>
  );
}
